import { CourseRole, OrgRole, SectionRole } from "./types/roles";
import { InteractionMode, type PagePermissionType } from "./types/websocket-auth";

export enum ComputedRole {
  ORG_ADMIN = "orgAdmin",
  INSTRUCTOR = "instructor",
  STUDENT = "student",
  SECTION_ASSISTANT = "sectionAssistant",
  COURSE_ADMIN = "courseAdmin",
  SUPERUSER = "superuser",
  RECORDING_AGENT = "recordingAgent",
  UNKNOWN = "unknown",
}

// Capabilities at the page level
export enum PageCapability {
  CAN_VIEW_PAGE = "canViewPage",
  CAN_VIEW_OTHER_USERS_DATA = "canViewOtherUsersData",
  CAN_MAKE_RESPONSE = "canMakeResponse",
  CAN_AUTHOR_PAGE = "canAuthorPage",
}

// Capabilities at the section level
export enum SectionCapability {
  CAN_AUTHOR_SECTION = "canAuthorSection",
  CAN_EDIT_STUDENT_DRAFT = "canEditStudentDraft",
  CAN_CREATE_CHAT_MESSAGE = "canCreateChatMessage",
  CAN_DELETE_CHAT_MESSAGE = "canDeleteChatMessage",
  CAN_MENTION_ANY_BREAKOUT_GROUP_IN_CHAT = "canMentionAnyBreakoutGroupInChat",
  CAN_MENTION_ALL_IN_CHAT = "canMentionAllInChat",
  CAN_PARTICIPATE_IN_SECTION = "canParticipateInSection",
  CAN_PRESENT_SECTION = "canPresentSection",
  CAN_START_AND_STOP_RECORDING = "canStartAndStopRecording",
  IS_VISIBLE_IN_CLASS = "isVisibleInClass",
}

// Capabilities at the org level
export enum OrgCapability {
  CAN_ACCESS_MONITORING_DASHBOARD = "canAccessMonitoringDashboard",
  CAN_AUTHOR_ORG = "canAuthorOrg",
  CAN_IMPERSONATE_USER = "canImpersonateUser",
  CAN_OBSERVE_VIRTUAL_CLASS = "canObserveVirtualClass",
  CAN_SEE_ALL_DOCS = "canSeeAllDocs",
  CAN_SEE_ALL_ORGS = "canSeeAllOrgs",
  CAN_SEE_ALL_ORG_USERS = "canSeeAllOrgUsers",
  CAN_USE_BETA_FEATURES = "canUseBetaFeatures",
  CAN_USE_SUPERUSER_FEATURES = "canUseSuperuserFeatures",
}

// Capabilities at the course level
export enum CourseCapability {
  CAN_AUTHOR_COURSE = "canAuthorCourse",
}

export const Capability = {
  ...SectionCapability,
  ...OrgCapability,
  ...CourseCapability,
};

export const ROLES_MAP: Record<SectionRole | OrgRole | CourseRole, ComputedRole> = {
  admin: ComputedRole.ORG_ADMIN,
  instructor: ComputedRole.INSTRUCTOR,
  section_assistant: ComputedRole.SECTION_ASSISTANT,
  course_admin: ComputedRole.COURSE_ADMIN,
  student: ComputedRole.STUDENT,
  recording_agent: ComputedRole.RECORDING_AGENT,
  other: ComputedRole.UNKNOWN,
  none: ComputedRole.UNKNOWN,
};

// Course admins get the same section-level capabilities as org admins, but only for
// sections whose course they're assigned to (via CourseUser or CourseGroupingUser).
// Keep this list mirrored with ORG_ADMIN entries below.
const SECTION_CAPABILITIES_MAP: Record<SectionCapability, ComputedRole[]> = {
  [Capability.CAN_AUTHOR_SECTION]: [
    ComputedRole.INSTRUCTOR,
    ComputedRole.SECTION_ASSISTANT,
    ComputedRole.COURSE_ADMIN,
    ComputedRole.SUPERUSER,
    ComputedRole.ORG_ADMIN,
  ],
  [Capability.CAN_EDIT_STUDENT_DRAFT]: [
    ComputedRole.INSTRUCTOR,
    ComputedRole.SECTION_ASSISTANT,
    ComputedRole.COURSE_ADMIN,
    ComputedRole.SUPERUSER,
    ComputedRole.ORG_ADMIN,
  ],
  [Capability.CAN_CREATE_CHAT_MESSAGE]: [
    ComputedRole.INSTRUCTOR,
    ComputedRole.SECTION_ASSISTANT,
    ComputedRole.COURSE_ADMIN,
    ComputedRole.SUPERUSER,
    ComputedRole.ORG_ADMIN,
    ComputedRole.STUDENT,
  ],
  [Capability.CAN_DELETE_CHAT_MESSAGE]: [
    ComputedRole.INSTRUCTOR,
    ComputedRole.SECTION_ASSISTANT,
    ComputedRole.COURSE_ADMIN,
    ComputedRole.SUPERUSER,
    ComputedRole.ORG_ADMIN,
  ],
  [Capability.CAN_MENTION_ANY_BREAKOUT_GROUP_IN_CHAT]: [
    ComputedRole.INSTRUCTOR,
    ComputedRole.SECTION_ASSISTANT,
    ComputedRole.COURSE_ADMIN,
    ComputedRole.SUPERUSER,
    ComputedRole.ORG_ADMIN,
  ],
  [Capability.CAN_MENTION_ALL_IN_CHAT]: [
    ComputedRole.INSTRUCTOR,
    ComputedRole.SECTION_ASSISTANT,
    ComputedRole.SUPERUSER,
    ComputedRole.ORG_ADMIN,
  ],
  [Capability.CAN_PARTICIPATE_IN_SECTION]: [ComputedRole.STUDENT],
  // For now, only instructors/section assistants can present
  [Capability.CAN_PRESENT_SECTION]: [ComputedRole.INSTRUCTOR, ComputedRole.SECTION_ASSISTANT],
  [Capability.CAN_START_AND_STOP_RECORDING]: [
    ComputedRole.INSTRUCTOR,
    ComputedRole.SECTION_ASSISTANT,
    ComputedRole.COURSE_ADMIN,
    ComputedRole.SUPERUSER,
    ComputedRole.ORG_ADMIN,
  ],
  [Capability.IS_VISIBLE_IN_CLASS]: [
    ComputedRole.INSTRUCTOR,
    ComputedRole.SECTION_ASSISTANT,
    ComputedRole.STUDENT,
  ],
};

const ORG_CAPABILITIES_MAP: Record<OrgCapability, ComputedRole[]> = {
  // TODO: remove org admin capability once mp admins have a none org role
  [Capability.CAN_ACCESS_MONITORING_DASHBOARD]: [ComputedRole.SUPERUSER],
  [Capability.CAN_AUTHOR_ORG]: [ComputedRole.ORG_ADMIN, ComputedRole.SUPERUSER],
  [Capability.CAN_IMPERSONATE_USER]: [ComputedRole.SUPERUSER],
  [Capability.CAN_SEE_ALL_DOCS]: [ComputedRole.RECORDING_AGENT],
  [Capability.CAN_SEE_ALL_ORGS]: [ComputedRole.SUPERUSER],
  [Capability.CAN_SEE_ALL_ORG_USERS]: [ComputedRole.SUPERUSER],
  [Capability.CAN_OBSERVE_VIRTUAL_CLASS]: [ComputedRole.ORG_ADMIN, ComputedRole.SUPERUSER],
  [Capability.CAN_USE_BETA_FEATURES]: [ComputedRole.SUPERUSER],
  [Capability.CAN_USE_SUPERUSER_FEATURES]: [ComputedRole.SUPERUSER],
};

const COURSE_CAPABILITIES_MAP: Record<CourseCapability, ComputedRole[]> = {
  [Capability.CAN_AUTHOR_COURSE]: [
    ComputedRole.COURSE_ADMIN,
    ComputedRole.ORG_ADMIN,
    ComputedRole.SUPERUSER,
  ],
};

export type GetComputedRoleParams = {
  isSuperuser: boolean;
  sectionRole?: SectionRole | null;
  orgRole?: OrgRole | null;
  courseRole?: CourseRole | null;
  isStudentPreview?: boolean;
};

export const getComputedRole = ({
  isSuperuser,
  sectionRole,
  orgRole,
  courseRole,
  isStudentPreview = false,
}: GetComputedRoleParams): ComputedRole => {
  if (isStudentPreview) {
    return ComputedRole.STUDENT;
  }

  const role = sectionRole || courseRole || orgRole;
  // MP Admin users might have a "none" role in the org, which allows them to have full MP Admin capabilities.
  if (role && role !== OrgRole.NONE) {
    return ROLES_MAP[role];
  }

  return isSuperuser ? ComputedRole.SUPERUSER : ComputedRole.UNKNOWN;
};

// Section capabilities
export const hasSectionCapability = (computedRole: ComputedRole, capability: SectionCapability) =>
  SECTION_CAPABILITIES_MAP[capability].includes(computedRole);

export const getSectionCapabilities = (computedRole: ComputedRole) => {
  return Object.fromEntries(
    Object.values(SectionCapability).map((capability) => [
      capability,
      hasSectionCapability(computedRole, capability),
    ]),
  ) as Record<SectionCapability, boolean>;
};

// Org capabilities
export const hasOrgCapability = (userOrgRole: ComputedRole, capability: OrgCapability) => {
  return ORG_CAPABILITIES_MAP[capability].includes(userOrgRole);
};

export const getOrgCapabilities = (userOrgRole: ComputedRole) => {
  return Object.fromEntries(
    Object.values(OrgCapability).map((capability) => [
      capability,
      hasOrgCapability(userOrgRole, capability),
    ]),
  ) as Record<OrgCapability, boolean>;
};

// Course capabilities
export const hasCourseCapability = (userCourseRole: ComputedRole, capability: CourseCapability) => {
  return COURSE_CAPABILITIES_MAP[capability].includes(userCourseRole);
};

export const getCourseCapabilities = (userCourseRole: ComputedRole) => {
  return Object.fromEntries(
    Object.values(CourseCapability).map((capability) => [
      capability,
      hasCourseCapability(userCourseRole, capability),
    ]),
  ) as Record<CourseCapability, boolean>;
};

// Page capabilities
const getCanViewPage = (page: PagePermissionType | null, computedRole: ComputedRole) => {
  const canSeeAllDocs = hasOrgCapability(computedRole, Capability.CAN_SEE_ALL_DOCS);
  const { canAuthorSection, canParticipateInSection } = getSectionCapabilities(computedRole);
  const isPageReleased = Boolean(page?.released_at);
  return canSeeAllDocs || canAuthorSection || (canParticipateInSection && isPageReleased);
};

const getCanViewOtherUsersData = (page: PagePermissionType | null, computedRole: ComputedRole) => {
  const canAuthorSection = hasSectionCapability(computedRole, Capability.CAN_AUTHOR_SECTION);
  const canViewPage = getCanViewPage(page, computedRole);
  const isPageVisibleToAll =
    canViewPage && Boolean(page?.last_rolled_up_at || page?.class_responses_released_at);
  return canAuthorSection || isPageVisibleToAll;
};

const getCanMakeResponse = (
  page: PagePermissionType | null,
  computedRole: ComputedRole,
  readyPageGroup: boolean,
) => {
  if (!page) {
    return false;
  }
  // Only students can make responses
  const canParticipateInSection = hasSectionCapability(
    computedRole,
    Capability.CAN_PARTICIPATE_IN_SECTION,
  );
  const canViewPage = getCanViewPage(page, computedRole);
  if (!canParticipateInSection || !canViewPage || page.locked_at) {
    return false;
  }
  const isGroupPage =
    page.interaction_mode &&
    [InteractionMode.ASSIGN_STUDENTS, InteractionMode.SELF_ASSIGN].includes(page.interaction_mode);

  // If it's a group page user has to be in a group and ready to make a response
  return !isGroupPage || readyPageGroup;
};

const getCanAuthorPage = (computedRole: ComputedRole) => {
  return hasSectionCapability(computedRole, Capability.CAN_AUTHOR_SECTION);
};

export const getPageCapabilities = (
  page: PagePermissionType | null,
  computedRole: ComputedRole,
  readyPageGroup: boolean,
): Record<PageCapability, boolean> => {
  return {
    [PageCapability.CAN_VIEW_PAGE]: getCanViewPage(page, computedRole),
    [PageCapability.CAN_VIEW_OTHER_USERS_DATA]: getCanViewOtherUsersData(page, computedRole),
    [PageCapability.CAN_MAKE_RESPONSE]: getCanMakeResponse(page, computedRole, readyPageGroup),
    [PageCapability.CAN_AUTHOR_PAGE]: getCanAuthorPage(computedRole),
  };
};

export const getPageCapabilitiesForServer = (
  page: PagePermissionType | null,
  computedRole: ComputedRole,
) => {
  return {
    [PageCapability.CAN_VIEW_PAGE]: getCanViewPage(page, computedRole),
    [PageCapability.CAN_VIEW_OTHER_USERS_DATA]: getCanViewOtherUsersData(page, computedRole),
  };
};
